import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI } from '../services/api';
import { Users, Building2, Search, Plus, Mail, Phone, MapPin, AlertCircle } from 'lucide-react';

const ClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState('all');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch data when filter or debounced search changes
  useEffect(() => {
    fetchData();
  }, [clientTypeFilter, debouncedSearch]);

  const fetchData = async () => {
    try {
      setError(null);
      // Show refreshing indicator if not initial load
      if (!loading) {
        setRefreshing(true);
      }

      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (clientTypeFilter !== 'all') params.client_type = clientTypeFilter;

      const [clientsRes, statsRes] = await Promise.all([
        clientAPI.getAll(params),
        clientAPI.getStats()
      ]);

      setClients(clientsRes.data.clients || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Failed to load clients. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Memoized stat cards
  const statCards = useMemo(() => [
    {
      title: 'Total Clients',
      value: stats?.total_clients || 0,
      icon: Users,
      gradient: 'from-slate-800 to-slate-900',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-900',
    },
    {
      title: 'Customers',
      value: stats?.total_customers || 0,
      subtitle: 'End buyers',
      icon: Users,
      gradient: 'from-emerald-500 to-emerald-600',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      title: 'Dealers',
      value: stats?.total_dealers || 0,
      subtitle: 'Trade partners',
      icon: Building2,
      gradient: 'from-amber-500 to-amber-600',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
    },
  ], [stats]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container flex flex-col items-center justify-center min-h-[50vh] text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Error loading clients</h3>
        <p className="text-slate-600 mb-6">{error}</p>
        <button onClick={fetchData} className="btn-primary">Try again</button>
      </div>
    );
  }

  return (
    <div className="page-container space-y-6">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="page-subtitle">Manage your customers and dealer network</p>
        </div>
        <button onClick={() => navigate('/clients/add')} className="btn-primary">
          <Plus className="w-5 h-5" />
          Add Client
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statCards.map((stat, index) => (
          <div key={index} className="card card-body">
            <div className="flex items-center justify-between">
              <div className={`p-3 rounded-xl ${stat.iconBg}`}>
                <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm font-medium text-slate-600">{stat.title}</p>
                {stat.subtitle && <p className="text-xs text-slate-500">{stat.subtitle}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card card-body relative">
        {refreshing && (
          <div className="absolute top-4 right-4">
            <div className="loading-spinner w-5 h-5 border-t-amber-500" />
          </div>
        )}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email, or company..."
              value={searchQuery}
              onChange={handleSearch}
              className="input-field pl-10"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'Customer', 'Dealer'].map((filter) => (
              <button
                key={filter}
                onClick={() => setClientTypeFilter(filter)}
                className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-colors ${
                  clientTypeFilter === filter
                    ? filter === 'all' ? 'bg-slate-800 text-white' : filter === 'Customer' ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {filter === 'all' ? 'All' : filter === 'Customer' ? 'Customers' : 'Dealers'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {clients.length === 0 ? (
        <div className="card card-body text-center py-16">
          <Users className="w-14 h-14 text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-900 mb-1">No clients yet</h3>
          <p className="text-slate-500 mb-6">Start building your client network</p>
          <button onClick={() => navigate('/clients/add')} className="btn-primary">Add your first client</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {clients.map((client) => (
            <div
              key={client.id}
              onClick={() => navigate(`/clients/${client.id}`)}
              className="card card-body cursor-pointer hover:shadow-card-hover transition-shadow"
            >
              {/* Client Type Badge */}
              <div className="flex items-center justify-between mb-4">
                <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                  client.client_type === 'Dealer'
                    ? 'bg-gradient-to-r from-amber-100 to-amber-50 text-amber-700'
                    : 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-700'
                }`}>
                  {client.client_type}
                </div>
                {client.client_type === 'Dealer' ? (
                  <Building2 className="w-6 h-6 text-amber-600" />
                ) : (
                  <Users className="w-6 h-6 text-emerald-600" />
                )}
              </div>

              {/* Client Name */}
              <h3 className="text-xl font-bold text-gray-900 mb-1">{client.name}</h3>
              {client.company && (
                <p className="text-sm text-gray-500 mb-3">{client.company}</p>
              )}

              {/* Contact Info */}
              <div className="space-y-2">
                {client.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{client.email}</span>
                  </div>
                )}
                {client.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span>{client.phone}</span>
                  </div>
                )}
                {client.city && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>{client.city}{client.state ? `, ${client.state}` : ''}</span>
                  </div>
                )}
              </div>

              {/* Tags */}
              {client.tags && client.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {client.tags.slice(0, 3).map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg"
                    >
                      {tag}
                    </span>
                  ))}
                  {client.tags.length > 3 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg">
                      +{client.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Budget Range (for customers) */}
              {client.client_type === 'Customer' && (client.budget_min || client.budget_max) && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">Budget Range</p>
                  <p className="text-sm font-semibold text-gray-900">
                    ${(client.budget_min || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} - ${(client.budget_max || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                </div>
              )}

              {/* Dealer Network (for dealers) */}
              {client.client_type === 'Dealer' && client.dealer_network && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">Network</p>
                  <p className="text-sm font-semibold text-gray-900">{client.dealer_network}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientsPage;
